import type { QuoteLineItemView } from "@/services/quoteLineItems";
import { apiClient } from "@/services/apiClient";
import type { UserNotarialRequestDocumentType } from "@/services/userNotarialRequestsService";
import { readInternalSource } from "@/lib/mag-analytics";

/**
 * A file plus the document type it was attached under. Paired rather than held
 * as two parallel arrays: the arrays only have to drift once for a document to
 * be filed as the wrong kind, and nothing downstream re-checks it.
 */
export interface QuoteRequestAttachmentUpload {
  file: File;
  /** Null for a file added to a generic slot. */
  documentType: string | null;
  /**
   * Which anticipated-document slot this file was attached to, or null for a
   * loose one. Client-side only — it identifies the slot in the UI so that
   * adding or removing a loose file cannot re-point a named slot at somebody
   * else's file. Never sent to the server.
   */
  slotLabel?: string | null;
}

/**
 * One anticipated item the consumer answered by typing (spec 13810fd2) — an
 * RUT, a número de BPS. Keyed by the intake's own label for the item, which is
 * how the professional's scope card pairs the answer back to the question.
 */
export interface QuoteRequestIntakeDetail {
  label: string;
  value: string;
}

export interface CreateQuoteRequestPayload {
  /**
   * The saved address this request's physical document goes to (spec
   * 8c2aab49 D3). Absent on digital jobs, which never see the address step.
   */
  deliveryAddressId?: string;
  title: string;
  description: string;
  attachments: QuoteRequestAttachmentUpload[];
  /**
   * Answers to the intake's text-kind items. Empty on the static fallback
   * form, which has no conversation and therefore no items.
   */
  intakeDetails?: QuoteRequestIntakeDetail[];
  /**
   * Set only when the conversational intake reached a draft. The backend then
   * skips the one-shot classifier and uses the conversation's own decision.
   *
   * Must stay unset after a fallback to the static form (503, or the consumer
   * opting out): that conversation never produced a draft, and sending its id
   * is rejected.
   */
  intakeConversationId?: string | null;
}

/**
 * One discipline's half of a request (spec 27c505ed). A single-discipline RFQ
 * has exactly one, saying the same thing as the top-level award/payment; a
 * two-discipline one has two, and then the per-leg view is the ONLY honest
 * one — there is no single award and no single amount.
 */
export interface QuoteRequestLegView {
  id: string;
  discipline: "notary" | "accountant";
  status: string;
  quotingExpiresAt: string | null;
  awardedQuoteId: string | null;
  award: QuoteRequestAward | null;
  payment: QuoteRequestPayment | null;
  childRequestId: string | null;
  childRequestType: string | null;
}

/** Subtotal, fee and total for an awarded request, as the server computes it. */
export interface QuoteRequestCharge {
  /** The professionals' awarded prices, added up. */
  subtotalMinor: number;
  /** Gestionalo's charge for running the transaction. 0 when none applies. IVA-inclusive. */
  serviceFeeMinor: number;
  /**
   * IVA on the professionals' honorarios, where they declared their price
   * excludes it. 0 when every awarded quote already includes it.
   */
  /**
   * Retained on the CHECKOUT preview, where it is now always 0: spec 76e23408
   * D6 stopped adding the professional's IVA to anyone's charge. The field
   * survives because it is persisted on the checkout row; surfaces hide it
   * when zero, so this reads as the line simply being gone.
   */
  professionalIvaMinor: number;
  /**
   * Gestionalo's charge to deliver the physical document, when any awarded
   * quote declares the job produces one. 0 for a fully digital request.
   * Charged ONCE per request even when both legs are physical — a
   * two-discipline job is still one package. Whether it carries IVA is the
   * platform rate's own stored flag, not an assumption made here.
   *
   * Always 0 on an already-settled checkout: what was actually charged is
   * stored as a single total, so the breakdown is folded into the subtotal
   * rather than recomputed from a rate an admin may since have changed.
   */
  deliveryFeeMinor: number;
  /** What is or was charged to the card: subtotal + professional IVA + fee + delivery. */
  totalMinor: number;
  currency: string;
}

export interface QuoteRequestSummary {
  id: string;
  status: string;
  title: string;
  description: string;
  discipline: string;
  awardedQuoteId: string | null;
  acceptedProfessionalUserId: string | null;
  childRequestId: string | null;
  childRequestType: "notarial_request" | "accountant_request" | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Timeline, oldest first. Already filtered server-side for this audience —
   * a professional never receives another professional's quote events.
   * Absent on list responses; only the detail endpoint returns it.
   */
  events?: QuoteRequestEvent[];
  /** Files the consumer attached at intake. Detail endpoint only. */
  attachments?: QuoteRequestAttachment[];
  award?: QuoteRequestAward | null;
  payment?: QuoteRequestPayment | null;
  /** Absent on list payloads; present on the detail. */
  legs?: QuoteRequestLegView[];
  /**
   * What the request will cost, or did — server-computed (spec
   * platform-fee-model §3d).
   *
   * Never re-derive this by summing the legs. That is what the legs card used
   * to do, and it was only ever right while the charge was exactly the sum of
   * the awards: it cannot know the platform's service fee, and once a checkout
   * exists the server reports what THAT row says rather than what today's fee
   * would produce. Null until something is awarded.
   */
  charge?: QuoteRequestCharge | null;
  /**
   * Questions professionals asked, and the consumer's answers. Detail endpoint
   * only, and never includes a message moderation stopped.
   */
  clarifications?: QuoteClarification[];
}

/** Kinds the contact-detail scan reports, in the order the copy lists them. */
export type ContactDetailKind = "email" | "phone" | "url" | "handle" | "contact_attempt";

/**
 * One question on a request, and its single answer (spec ce646966, with the
 * public-thread decision later reversed).
 *
 * Threads are PRIVATE per professional: a professional sees only their own
 * questions. The consumer sees all of them but still cannot tell who asked —
 * `askerOrdinal` is a stable anonymous handle, not an identity.
 */
export interface QuoteClarification {
  id: string;
  question: string;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
  /** Set only on the reader's own questions. */
  isMine?: boolean;
  /**
   * Stable anonymous number per asking professional, so the consumer can tell
   * one private conversation from another. Consumer responses only.
   */
  askerOrdinal?: number;
  /**
   * Set only on the author's own blocked messages, so a question that never
   * reached the consumer is visibly blocked rather than silently missing.
   */
  blocked?: {
    reason: "contact_details" | "ai_flagged";
    kinds: ContactDetailKind[];
  };
}

/**
 * The contact-detail kinds a rejected message tripped, or null if the failure
 * was anything else.
 *
 * Moderation answers with 400 `contact_details_blocked`; the caller needs the
 * kinds to tell the author what to remove, and a generic error toast for
 * everything else.
 */
export function contactDetailKindsFromError(error: unknown): ContactDetailKind[] | null {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data as
    | { message?: unknown; detected?: unknown }
    | undefined;
  if (data?.message !== "contact_details_blocked") return null;
  const detected = Array.isArray(data.detected) ? data.detected : [];
  const kinds = detected.filter((k): k is ContactDetailKind =>
    k === "email" || k === "phone" || k === "url" || k === "handle" ||
    // The AI layer has no matched substring to report — that is the point of
    // it — so it names the behaviour instead of a pattern.
    k === "contact_attempt",
  );
  // An empty list still means blocked — the copy falls back to a generic
  // reason rather than pretending the message went through.
  return kinds;
}

/**
 * Did the server refuse this quote because the professional has not declared
 * their fiscal data?
 *
 * Detected by errorCode rather than by message text, because the response to
 * it is to OPEN A FORM — the professional resolves it in the same interaction
 * and sends. A generic toast here would be an outage dressed as an error: no
 * professional currently holds a RUT, so every one of them meets this on their
 * next quote, and the only thing standing between a gate and a dead
 * marketplace is that this returns true and the form appears.
 */
export function isFiscalDeclarationRequiredError(error: unknown): boolean {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data as
    | { errorCode?: unknown }
    | undefined;
  return data?.errorCode === "fiscal_declaration_required";
}

export function answerQuoteRequestClarification(
  quoteRequestId: string,
  clarificationId: string,
  answer: string,
): Promise<QuoteClarification> {
  return apiClient
    .post<QuoteClarification>(
      `/me/quote-requests/${quoteRequestId}/clarifications/${clarificationId}/answer`,
      { answer },
    )
    .then((res) => res.data);
}

export interface QuoteRequestAttachment {
  id: string;
  originalFilename: string;
  contentType: string | null;
  /** Reuses the notarial document vocabulary; see the backend migration note. */
  documentType: UserNotarialRequestDocumentType;
  createdAt: string;
}

/** The winning bid, present once the RFQ is awarded. */
export interface QuoteRequestAward {
  priceMinor: number;
  currency: string;
  estimatedDays: number | null;
  professionalDisplayName: string | null;
  /**
   * Register credentials of the professional the consumer committed to.
   * Deliberately no email: bidders stay anonymous by handle until award so the
   * job cannot go off-platform, and award is not payment — contact details
   * arrive with the child request once the checkout clears.
   */
  professionalCedula: string | null;
  professionalMatricula: string | null;
  /** Presence flag and cache key — the bytes come from an authenticated route. */
  professionalProfileImage: string | null;
}

/** The quote-priced checkout, present once one has been created. */
export interface QuoteRequestPayment {
  amountMinor: number;
  currency: string;
  status: string;
  paidAt: string | null;
}

export async function addQuoteRequestAttachment(
  quoteRequestId: string,
  file: File,
  documentType: UserNotarialRequestDocumentType,
): Promise<QuoteRequestAttachment> {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("documentType", documentType);
  const res = await apiClient.post<QuoteRequestAttachment>(
    `/me/quote-requests/${quoteRequestId}/attachments`,
    formData,
  );
  return res.data;
}

export async function deleteQuoteRequestAttachment(
  quoteRequestId: string,
  attachmentId: string,
): Promise<void> {
  await apiClient.delete(`/me/quote-requests/${quoteRequestId}/attachments/${attachmentId}`);
}

/**
 * Attachments live in private Blob storage, so there is no direct URL to link.
 * The file is proxied through the API and must be fetched with credentials,
 * then handed to the browser as an object URL.
 */
export async function downloadQuoteRequestAttachment(
  quoteRequestId: string,
  attachmentId: string,
): Promise<Blob> {
  const res = await apiClient.get<Blob>(
    `/me/quote-requests/${quoteRequestId}/attachments/${attachmentId}/file`,
    { responseType: "blob" },
  );
  return res.data;
}

export interface QuoteRequestEvent {
  id: string;
  eventType: string;
  actorDisplayName: string | null;
  actorRole: string | null;
  message: string | null;
  createdAt: string;
  /**
   * The discipline leg this event belongs to, or null when it is about the
   * request as a whole. Already on the wire — the entity column is serialized
   * straight through `filterQuoteEventsForConsumer` — and declared here so the
   * timeline can tell a two-leg broadcast apart from a duplicate.
   */
  legId: string | null;
}

/** A professional's bid, as shown to the consumer reviewing their own request. */
export interface QuoteForConsumer {
  id: string;
  /**
   * The discipline leg this bid is on. A two-discipline RFQ returns BOTH legs'
   * quotes in one list, so without grouping by this an escribano's price and a
   * contador's price render side by side as if they were competing offers for
   * the same work.
   */
  legId: string;
  /**
   * Would awarding THIS bid be refused for want of a delivery destination?
   *
   * Per quote rather than per request, because the server gate is scoped to a
   * leg: on a two-discipline RFQ a digital accountant bid and a physical
   * notary bid are separate decisions, and only the second owes an address.
   *
   * Advisory. `awardQuote` refuses on its own and must — this page can be
   * stale, and a professional can declare physical between it loading and the
   * award landing.
   */
  needsDeliveryAddress: boolean;
  professionalUserId: string;
  /**
   * The professional's real name, revealed ONLY once this quote is awarded.
   * Null before then — the consumer chooses on price and time, and a name is
   * the one field that makes taking the job off-platform trivial.
   */
  professionalDisplayName: string | null;
  /** Anonymous, stable handle shared with the clarification threads and the timeline. */
  professionalOrdinal: number | null;
  priceMinor: number;
  /** Platform fee the consumer pays on top of priceMinor. Computed server-side. IVA-inclusive. */
  consumerFeeMinor: number;
  /**
   * IVA on the professional's honorarios, when they declared their price
   * excludes it. Zero when they declared it included. Its own field so the
   * breakdown adds up: priceMinor + this + consumerFeeMinor === totalMinor.
   */
  /**
   * The IVA already INSIDE the professional's price (spec 76e23408 D6).
   * NOT a term of totalMinor — the breakdown decomposes rather than adds, so
   * rendering it as its own row would stop the lines summing to the total.
   */
  professionalIvaIncludedMinor: number;
  /**
   * Gestionalo's charge to deliver the physical document, when this
   * professional declared the job produces one. Charged ONCE per request, so a
   * second physical bid on a request already covered by an awarded leg reports
   * 0 — the award dialog must not promise a fee the card will not take.
   */
  deliveryFeeMinor: number;
  /** What awarding this quote actually costs, delivery included. IVA not included. */
  totalMinor: number;
  /** The itemisation behind the total. Null on quotes submitted before breakdowns existed. */
  lineItems: QuoteLineItemView[] | null;
  /** Declarative only — the total is always the final amount charged. */
  pricesIncludeIva: boolean;
  currency: string;
  estimatedDays: number | null;
  message: string | null;
  /**
   * What this professional says they need from you to do the job (spec
   * 18c82ff1). Comparable between bids in a way the free-text message never
   * was — four documents against two is a real difference between two offers.
   *
   * Empty on every quote submitted before the field existed, which renders as
   * no section rather than an empty heading.
   */
  requiredDocuments: QuoteRequiredDocumentForConsumer[];
  status: "submitted" | "awarded" | "rejected";
  createdAt: string;
}

/** One thing a professional declared they need. See `requiredDocuments`. */
export interface QuoteRequiredDocumentForConsumer {
  label: string;
  /** 'file' is something to find and upload; 'text' is a detail to type. */
  kind: "file" | "text";
  documentType: string | null;
  /**
   * Whether the platform anticipated this at intake or the professional added
   * it. Not rendered today — kept because the server already knows it and a
   * later copy change may want to say "también te va a pedir".
   */
  source: "anticipated" | "professional";
}

export function createQuoteRequest(payload: CreateQuoteRequestPayload): Promise<QuoteRequestSummary> {
  const formData = new FormData();
  formData.append("title", payload.title);
  formData.append("description", payload.description);
  payload.attachments.forEach(({ file }) => formData.append("files", file));
  if (payload.intakeConversationId) {
    formData.append("intakeConversationId", payload.intakeConversationId);
  }
  // Appended explicitly, like every other field: this is multipart, so a value
  // sitting on the payload object reaches the server only if something puts it
  // in the FormData. The field was on the payload type and silently absent
  // from the request until this line existed.
  if (payload.deliveryAddressId) {
    formData.append("deliveryAddressId", payload.deliveryAddressId);
  }
  // Read here rather than threaded through the wizard's payload, because this
  // has to survive a login round-trip that leaves and re-enters the app.
  // sessionStorage does; component state does not, which is the whole reason
  // the post-SSO resume blob exists. It is ambient session context, not
  // something the consumer typed, so it does not belong in the payload type.
  const entrySource = readInternalSource();
  if (entrySource) {
    formData.append("entrySource", entrySource);
  }
  if (payload.attachments.length > 0) {
    // JSON, not repeated fields: a repeated multipart field collapses to a
    // bare string when there is exactly one, and cannot carry a null at all.
    // One entry per file, including the untyped ones — the server rejects a
    // length mismatch rather than zipping.
    formData.append(
      "documentTypes",
      JSON.stringify(payload.attachments.map((a) => a.documentType)),
    );
  }
  if (payload.intakeDetails && payload.intakeDetails.length > 0) {
    // Same encoding argument as documentTypes, and for a second reason: the
    // server has to hold these before it moderates, since they reach every
    // professional in the discipline exactly as the description does.
    formData.append("details", JSON.stringify(payload.intakeDetails));
  }
  return apiClient
    .post<QuoteRequestSummary>("/me/quote-requests", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function getMyQuoteRequestDetail(id: string): Promise<QuoteRequestSummary> {
  return apiClient.get<QuoteRequestSummary>(`/me/quote-requests/${id}`).then((res) => res.data);
}

/**
 * Gives a lapsed RFQ another seven days (spec 37f4b483).
 *
 * The window length is the server's, not a parameter: the person tapping
 * this has a request that already stalled, and a date picker is one more
 * decision between them and a live request. Refused, not no-op'd, when
 * nothing on the request has actually lapsed.
 */
export function extendMyQuoteRequest(
  id: string,
): Promise<{ extendedLegs: number; quotingExpiresAt: string }> {
  return apiClient
    .post<{ extendedLegs: number; quotingExpiresAt: string }>(
      `/me/quote-requests/${id}/extend`,
    )
    .then((res) => res.data);
}

export function cancelMyQuoteRequest(id: string): Promise<void> {
  return apiClient.delete(`/me/quote-requests/${id}`).then(() => undefined);
}

export function listMyQuoteRequestQuotes(id: string): Promise<QuoteForConsumer[]> {
  return apiClient.get<QuoteForConsumer[]>(`/me/quote-requests/${id}/quotes`).then((res) => res.data);
}

/**
 * Give an existing request a delivery destination.
 *
 * Only an id: a NEW address is created through `/me/addresses` first, so there
 * stays one validated, owned, Place-derived path that writes an address.
 */
export async function setMyQuoteRequestDeliveryAddress(
  id: string,
  deliveryAddressId: string,
): Promise<void> {
  await apiClient.post(`/me/quote-requests/${id}/delivery-address`, { deliveryAddressId });
}

export function awardMyQuoteRequestQuote(id: string, quoteId: string): Promise<QuoteRequestSummary> {
  return apiClient
    .post<QuoteRequestSummary>(`/me/quote-requests/${id}/award`, { quoteId })
    .then((res) => res.data);
}

export interface QuoteCheckoutCreateResponse {
  checkoutId: string;
  redirectUrl: string | null;
  status: "pending" | "paid";
  amountMinor: number;
  currency: string;
  /**
   * Which provider takes this payment, decided server-side at creation. An
   * awarded RFQ is always settled by card, so this is "dlocal" or
   * "mercadopago" — and the two need different things from the client:
   * follow `redirectUrl`, or render the Payment Brick.
   */
  paymentMethod: "dlocal" | "mercadopago" | "bank_transfer";
}

/**
 * legId is required once a request has more than one leg — the server refuses
 * to guess which discipline is being paid rather than defaulting to one.
 */
export function createMyQuoteRequestCheckout(
  id: string,
  legId?: string,
  /**
   * How the factura should be addressed. Sent at CHECKOUT because that is
   * where the catalog wizards ask too — their summary step, immediately
   * before paying — and an RFQ has no price to invoice until it is awarded.
   *
   * Omitted entirely by callers that do not collect it (the mobile app), and
   * the server then leaves the stored preference alone rather than resetting
   * it to consumidor final.
   */
  invoice?: { includeRut: boolean; rutDigits: string | null },
): Promise<QuoteCheckoutCreateResponse> {
  return apiClient
    .post<QuoteCheckoutCreateResponse>(`/me/quote-requests/${id}/checkout`, {
      ...(legId ? { legId } : {}),
      ...(invoice
        ? {
            invoiceIncludeRut: invoice.includeRut,
            ...(invoice.includeRut && invoice.rutDigits
              ? { invoiceRutDigits: invoice.rutDigits }
              : {}),
          }
        : {}),
    })
    .then((res) => res.data);
}

export function rejectMyQuoteRequestQuote(id: string, quoteId: string): Promise<void> {
  return apiClient.post(`/me/quote-requests/${id}/quotes/${quoteId}/reject`).then(() => undefined);
}


/** The zone and price for a delivery address, before the request exists. */
export interface QuoteDeliveryPreview {
  /** Null when the address cannot be zoned — the step blocks on it (C-3). */
  zone: string | null;
  feeMinor: number;
  includesIva: boolean;
}

export function previewQuoteDelivery(params: {
  adminArea?: string | null;
  countryCode?: string | null;
}): Promise<QuoteDeliveryPreview> {
  return apiClient
    .get<QuoteDeliveryPreview>("/me/quote-requests/delivery-preview", {
      params: {
        adminArea: params.adminArea ?? undefined,
        countryCode: params.countryCode ?? undefined,
      },
    })
    .then((res) => res.data);
}
